/**
 * Utility functions for efficiently reading files using streams.
 * Reads files in chunks and processes them line by line, avoiding
 * the need to load the entire file into memory.
 */

/**
 * Reads the first N lines of a file efficiently using streams.
 * Stops reading as soon as N lines are collected.
 *
 * @param file - The File object to read from
 * @param lineCount - Number of lines to read
 * @returns Promise resolving to an array of lines
 *
 * @example
 * ```ts
 * // Read header and first 10 data rows
 * const lines = await readFileLines(file, 11);
 * const header = lines[0];
 * const dataRows = lines.slice(1);
 * ```
 */
export async function readFileLines(file: File, lineCount: number): Promise<string[]> {
  const reader = file.stream().getReader();
  const decoder = new TextDecoder();
  const collectedLines: string[] = [];
  let buffer = '';

  try {
    while (collectedLines.length < lineCount) {
      const { done, value } = await reader.read();

      if (done) {
        // File ended - add remaining buffer as last line if not empty
        if (buffer.length > 0) {
          collectedLines.push(buffer);
        }
        break;
      }

      // Decode the chunk and append to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process all complete lines in the buffer
      const lines = buffer.split(/\r?\n/);
      // Keep the last incomplete line in the buffer
      buffer = lines.pop() ?? '';

      // Collect complete lines until we have enough
      for (const line of lines) {
        collectedLines.push(line);
        if (collectedLines.length >= lineCount) {
          break;
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  return collectedLines;
}
